import { LeadMagnetIdentity, LeadMagnetSnapshot, LeadMagnetState } from '@/types/lead-magnet';

import { LEAD_MAGNET_SNAPSHOT_TTL_MS, LEAD_MAGNET_SNAPSHOT_VERSION } from './constants';

const isSnapshotShape = (value: unknown): value is LeadMagnetSnapshot => {
	if (!value || typeof value !== 'object') return false;

	const snapshot = value as Partial<LeadMagnetSnapshot>;

	return (
		typeof snapshot.version === 'number' &&
		typeof snapshot.stage === 'string' &&
		typeof snapshot.currentIndex === 'number' &&
		typeof snapshot.structureHash === 'string' &&
		typeof snapshot.updatedAt === 'number' &&
		!!snapshot.answers &&
		typeof snapshot.answers === 'object'
	);
};

export const clearSnapshot = ({ storageKey }: LeadMagnetIdentity) => {
	if (typeof window === 'undefined') return;

	try {
		window.sessionStorage.removeItem(storageKey);
	} catch {
		// Storage is unavailable — nothing was written, nothing to clear.
	}
};

export const readSnapshot = (identity: LeadMagnetIdentity): LeadMagnetSnapshot | null => {
	if (typeof window === 'undefined') return null;

	try {
		const raw = window.sessionStorage.getItem(identity.storageKey);

		if (!raw) return null;

		const parsed: unknown = JSON.parse(raw);

		if (!isSnapshotShape(parsed)) return null;

		const isStale = Date.now() - parsed.updatedAt > LEAD_MAGNET_SNAPSHOT_TTL_MS;
		const isOtherStructure = parsed.structureHash !== identity.structureHash;

		if (parsed.version !== LEAD_MAGNET_SNAPSHOT_VERSION || isStale || isOtherStructure) {
			clearSnapshot(identity);

			return null;
		}

		return parsed;
	} catch {
		clearSnapshot(identity);

		return null;
	}
};

const isWorthPersisting = (state: LeadMagnetState) =>
	state.stage !== 'intro' || Object.keys(state.answers).length > 0;

export const writeSnapshot = (state: LeadMagnetState, identity: LeadMagnetIdentity) => {
	if (typeof window === 'undefined') return;

	if (!isWorthPersisting(state)) {
		clearSnapshot(identity);

		return;
	}
	// Do not persist submitStatus: reloading during submit must restore as idle.
	const snapshot: LeadMagnetSnapshot = {
		version: LEAD_MAGNET_SNAPSHOT_VERSION,
		stage: state.stage,
		currentIndex: state.currentIndex,
		answers: state.answers,
		leadSubmitted: state.leadSubmitted,
		structureHash: identity.structureHash,
		updatedAt: Date.now()
	};

	try {
		window.sessionStorage.setItem(identity.storageKey, JSON.stringify(snapshot));
	} catch {
		// Quota or private mode — the quiz keeps working, it just won't survive a reload.
	}
};
